import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, Bot, Globe } from 'lucide-react';
import surveysApi from '../services/surveysApi.js';

const PAGE_SIZE = 50;

const Stars = ({ value }) => {
  const filled = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className="flex items-center gap-0.5" aria-label={`${filled} из 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < filled ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'}
        />
      ))}
    </div>
  );
};

const SourceBadge = ({ source }) => {
  const isBot = source === 'bot';
  const Icon = isBot ? Bot : Globe;
  const cls = isBot
    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon size={12} />
      {isBot ? 'Бот' : 'WebApp'}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const SurveyResponsesModal = ({ open, onClose }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (nextOffset) => {
    setLoading(true);
    setError(null);
    try {
      const data = await surveysApi.list({ limit: PAGE_SIZE, offset: nextOffset });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } catch (e) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load(0);
      setExpanded({});
    }
  }, [open, load]);

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ duration: 0.18 }}
            className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Опросник удовлетворённости{total ? ` · ${total}` : ''}
              </h3>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Закрыть"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto px-3 sm:px-5 py-3">
              {loading && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Загрузка…</div>
              )}
              {error && (
                <div className="text-center py-8 text-red-500">{error}</div>
              )}
              {!loading && !error && items.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  Пока нет ни одного отзыва.
                </div>
              )}
              {!loading && !error && items.length > 0 && (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {items.map((it) => {
                    const open = !!expanded[it.id];
                    const fb = it.feedback || '';
                    const showToggle = fb.length > 160;
                    const display = open || !showToggle ? fb : `${fb.slice(0, 160)}…`;
                    return (
                      <li key={it.id} className="py-3">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {it.name || it.username || `ID ${it.user_id}`}
                              </span>
                              {it.username && (
                                <span className="text-xs text-gray-500">@{it.username}</span>
                              )}
                              <span className="text-xs text-gray-400">#{it.user_id}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Stars value={it.rating} />
                              <SourceBadge source={it.trigger_source} />
                              <span className="text-xs text-gray-500">{formatDate(it.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        {fb && (
                          <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {display}
                            {showToggle && (
                              <button
                                onClick={() => toggleExpand(it.id)}
                                className="ml-1 text-primary-600 dark:text-primary-400 hover:underline"
                              >
                                {open ? 'свернуть' : 'развернуть'}
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {total > PAGE_SIZE && (
              <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between">
                <button
                  disabled={offset === 0 || loading}
                  onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
                  className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Назад
                </button>
                <span className="text-xs text-gray-500">
                  {currentPage} / {totalPages}
                </span>
                <button
                  disabled={offset + PAGE_SIZE >= total || loading}
                  onClick={() => load(offset + PAGE_SIZE)}
                  className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Вперёд
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SurveyResponsesModal;
